import type { Node, Edge, Viewport } from 'reactflow';
import type { Folder, DiagramMetadata, PublicDiagram, ShareToggleResponse, StarToggleResponse } from '../types';
import {
  trackDiagramCreated,
  trackDiagramDeleted,
} from '../lib/posthog';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// Diagram with full data (for editor)
export interface Diagram {
  id: string;
  name: string;
  description: string | null;
  folderId: string | null;
  nodes: Node[];
  edges: Edge[];
  viewport: Viewport | null;
  userId: string | null;
  isPublic: boolean;
  shareToken: string | null;
  icon: string | null;
  iconColor: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiagramData {
  name: string;
  description?: string;
  nodes: Node[];
  edges: Edge[];
  viewport?: Viewport;
  folderId?: string | null;
  icon?: string | null;
  iconColor?: string | null;
}

export interface UpdateDiagramData {
  name?: string;
  description?: string;
  nodes?: Node[];
  edges?: Edge[];
  viewport?: Viewport;
  folderId?: string | null;
  icon?: string | null;
  iconColor?: string | null;
}

export interface DiagramListResponse {
  diagrams: DiagramMetadata[];
}

export interface FolderListResponse {
  folders: Folder[];
}

export interface CreateFolderData {
  name: string;
  color?: string;
  icon?: string;
}

export interface UpdateFolderData {
  name?: string;
  color?: string;
  icon?: string;
}

export class DiagramServiceError extends Error {
  constructor(
    message: string,
    public status: number,
    public isAuthError: boolean = false
  ) {
    super(message);
    this.name = 'DiagramServiceError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    throw new DiagramServiceError('Unauthorized', 401, true);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new DiagramServiceError(
      errorData.error || `Request failed with status ${response.status}`,
      response.status
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// ==================== DIAGRAM OPERATIONS ====================

/**
 * List all diagrams for the authenticated user
 * @param folderId - Optional filter by folder ID, pass 'null' string for uncategorized
 * @param forceRefresh - Skip browser cache to get fresh data
 */
export async function listDiagrams(folderId?: string | null, forceRefresh = false): Promise<DiagramMetadata[]> {
  let url = `${API_URL}/api/diagrams`;
  
  if (folderId !== undefined) {
    url += `?folderId=${folderId === null ? 'null' : folderId}`;
  }

  // Add cache-busting timestamp when forcing refresh
  if (forceRefresh) {
    url += folderId !== undefined ? '&_=' : '?_=';
    url += Date.now();
  }

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const data = await handleResponse<DiagramListResponse>(response);
  return data.diagrams;
}

/**
 * Get a specific diagram by ID
 * @param forceRefresh - Skip browser cache to get fresh data
 */
export async function getDiagram(id: string, forceRefresh = false): Promise<Diagram> {
  let url = `${API_URL}/api/diagrams/${id}`;
  
  // Add cache-busting timestamp when forcing refresh
  if (forceRefresh) {
    url += `?_=${Date.now()}`;
  }
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return handleResponse<Diagram>(response);
}

/**
 * Create a new diagram
 */
export async function createDiagram(data: CreateDiagramData): Promise<Diagram> {
  const response = await fetch(`${API_URL}/api/diagrams`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  const diagram = await handleResponse<Diagram>(response);
  trackDiagramCreated(diagram.id, 'manual');
  return diagram;
}

/**
 * Update an existing diagram (full update)
 */
export async function updateDiagram(
  id: string,
  data: UpdateDiagramData
): Promise<Diagram> {
  const response = await fetch(`${API_URL}/api/diagrams/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  return handleResponse<Diagram>(response);
}

/**
 * Partially update a diagram
 */
export async function patchDiagram(
  id: string,
  data: UpdateDiagramData
): Promise<Diagram> {
  const response = await fetch(`${API_URL}/api/diagrams/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  return handleResponse<Diagram>(response);
}

/**
 * Delete a diagram
 */
export async function deleteDiagram(id: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/diagrams/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  await handleResponse<void>(response);
  trackDiagramDeleted(id);
}

/**
 * Duplicate a diagram
 */
export async function duplicateDiagram(id: string, name?: string): Promise<Diagram> {
  // First get the original diagram
  const originalDiagram = await getDiagram(id);
  
  // Create a new diagram with the same data
  const duplicateData: CreateDiagramData = {
    name: name || `${originalDiagram.name} (Copy)`,
    description: originalDiagram.description || undefined,
    nodes: originalDiagram.nodes,
    edges: originalDiagram.edges,
    viewport: originalDiagram.viewport || undefined,
    folderId: originalDiagram.folderId || undefined,
  };

  const response = await fetch(`${API_URL}/api/diagrams`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(duplicateData),
  });

  const diagram = await handleResponse<Diagram>(response);
  trackDiagramCreated(diagram.id, 'duplicate');
  return diagram;
}

/**
 * Move a diagram to a folder
 */
export async function moveDiagramToFolder(
  diagramId: string,
  folderId: string | null
): Promise<Diagram> {
  const response = await fetch(`${API_URL}/api/diagrams/${diagramId}/move`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ folderId }),
  });

  return handleResponse<Diagram>(response);
}

// ==================== FOLDER OPERATIONS ====================

/**
 * List all folders for the authenticated user
 * @param forceRefresh - Skip browser cache to get fresh data
 */
export async function listFolders(forceRefresh = false): Promise<Folder[]> {
  let url = `${API_URL}/api/folders`;
  
  // Add cache-busting timestamp when forcing refresh
  if (forceRefresh) {
    url += `?_=${Date.now()}`;
  }
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const data = await handleResponse<FolderListResponse>(response);
  return data.folders;
}

/**
 * Get a specific folder by ID
 * @param forceRefresh - Skip browser cache to get fresh data
 */
export async function getFolder(id: string, forceRefresh = false): Promise<Folder> {
  let url = `${API_URL}/api/folders/${id}`;
  
  // Add cache-busting timestamp when forcing refresh
  if (forceRefresh) {
    url += `?_=${Date.now()}`;
  }
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return handleResponse<Folder>(response);
}

/**
 * Create a new folder
 */
export async function createFolder(data: CreateFolderData): Promise<Folder> {
  const response = await fetch(`${API_URL}/api/folders`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  return handleResponse<Folder>(response);
}

/**
 * Update a folder
 */
export async function updateFolder(
  id: string,
  data: UpdateFolderData
): Promise<Folder> {
  const response = await fetch(`${API_URL}/api/folders/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  return handleResponse<Folder>(response);
}

/**
 * Delete a folder (diagrams are moved to uncategorized)
 */
export async function deleteFolder(id: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/folders/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  await handleResponse<void>(response);
}

// ==================== SHARING OPERATIONS ====================

/**
 * Toggle public sharing for a diagram
 */
export async function toggleDiagramSharing(
  id: string,
  isPublic: boolean
): Promise<ShareToggleResponse> {
  const response = await fetch(`${API_URL}/api/diagrams/${id}/share`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ isPublic }),
  });

  return handleResponse<ShareToggleResponse>(response);
}

/**
 * Regenerate share token for a diagram
 */
export async function regenerateShareToken(id: string): Promise<ShareToggleResponse> {
  const response = await fetch(`${API_URL}/api/diagrams/${id}/share/regenerate`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return handleResponse<ShareToggleResponse>(response);
}

/**
 * Get a public diagram by share token (no auth required)
 */
export async function getPublicDiagram(shareToken: string): Promise<PublicDiagram> {
  const response = await fetch(`${API_URL}/api/public/diagrams/${shareToken}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new DiagramServiceError(
      errorData.error || 'Diagram not found or not public',
      response.status
    );
  }

  return response.json();
}

// ==================== HELPERS ====================

/**
 * Helper to check if an error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  return error instanceof DiagramServiceError && error.isAuthError;
}

// ==================== STARRING OPERATIONS ====================

/**
 * Toggle star status for a diagram
 */
export async function toggleDiagramStar(
  id: string,
  isStarred: boolean
): Promise<StarToggleResponse> {
  const response = await fetch(`${API_URL}/api/diagrams/${id}/star`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ isStarred }),
  });

  return handleResponse<StarToggleResponse>(response);
}

/**
 * Update a diagram's icon (Solar icon key) and hex color.
 */
export async function updateDiagramIcon(
  id: string,
  icon: string | null,
  iconColor: string | null
): Promise<Diagram> {
  const response = await fetch(`${API_URL}/api/diagrams/${id}/icon`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ icon, iconColor }),
  });

  return handleResponse<Diagram>(response);
}

// Re-export types for convenience
export type { DiagramMetadata, Folder, PublicDiagram, ShareToggleResponse, StarToggleResponse } from '../types';
